import { useState, useEffect, useRef } from 'react'
import { colors, text, space, radius } from '../design/tokens'
import Boton from './Boton'
import Paginador from './Paginador'
import { useFilaLink } from '../hooks/useFilaLink'

const ANCHO_DEFAULT = 120

/**
 * Tabla ABM del sistema. Extras para maestros anchos (clientes/artículos):
 *  - scrollX: scroll horizontal con anchos por columna (col.w)
 *  - Filtros por columna (col.filtrable): texto con debounce, server-side
 *  - Filtros MULTI (col.filtroMultiple + fetchFacetas): desplegable con los
 *    valores reales de la base, selección múltiple (whereIn)
 *  - Columnas fijas: 📌 en el header fija todas las columnas hasta esa
 *    (estilo Excel), persiste por pantalla en localStorage
 *  - "Limpiar filtros" vive en la barra superior: nunca se va de la pantalla
 */
export default function AbmTable({ title, columns, rows, onCreate, onEdit, onDelete, loading, filaHref, pagination, onPageChange, searchValue, onSearchChange, searchPlaceholder, scrollX = false, columnFilters, onColumnFilter, fetchFacetas, onExport,
  // Selección de filas (opcional). `seleccion` es un Set de ids; la página
  // decide qué hacer con lo elegido — la tabla solo se encarga de elegir.
  seleccionables = false, seleccion, onToggleFila, onToggleTodas }) {
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [filtrosLocal, setFiltrosLocal] = useState({})
  const [facetaAbierta, setFacetaAbierta] = useState(null) // key de la columna con el desplegable abierto
  const [facetas, setFacetas] = useState({})               // { key: [valores] }
  const [facetaBusqueda, setFacetaBusqueda] = useState('')
  const [seleccionLocal, setSeleccionLocal] = useState([]) // tildes del panel abierto — se ejecutan recién con "Aplicar"
  const [pinHasta, setPinHasta] = useState(() => {
    try { return localStorage.getItem(`lgr.pin.${title}`) || null } catch { return null }
  })
  const debounces = useRef({})

  const showSearch = typeof onSearchChange === 'function'
  const showFilters = typeof onColumnFilter === 'function'

  // ── columnas fijas (sticky) ──
  const pinIndex = pinHasta ? columns.findIndex(c => c.key === pinHasta) : -1
  const offsets = []
  let acc = 0
  columns.forEach((col, i) => {
    offsets[i] = acc
    acc += col.w || ANCHO_DEFAULT
  })
  const stickyCol = (i, zBase) => (scrollX && i <= pinIndex)
    ? { position: 'sticky', left: offsets[i], zIndex: zBase, boxShadow: i === pinIndex ? '2px 0 4px rgba(15,23,42,0.08)' : undefined }
    : {}

  const togglePin = (key) => {
    const next = pinHasta === key ? null : key
    setPinHasta(next)
    try {
      if (next) localStorage.setItem(`lgr.pin.${title}`, next)
      else localStorage.removeItem(`lgr.pin.${title}`)
    } catch { /* sin localStorage */ }
  }

  // ── filtros ──
  const handleFiltroTexto = (key, value) => {
    setFiltrosLocal(prev => ({ ...prev, [key]: value }))
    clearTimeout(debounces.current[key])
    debounces.current[key] = setTimeout(() => onColumnFilter(key, value), 400)
  }

  const toggleFacetaValor = (valor) => {
    setSeleccionLocal(prev => prev.includes(valor) ? prev.filter(v => v !== valor) : [...prev, valor])
  }

  const abrirFaceta = async (key) => {
    if (facetaAbierta === key) { setFacetaAbierta(null); return }
    setFacetaAbierta(key)
    setFacetaBusqueda('')
    setSeleccionLocal(Array.isArray(columnFilters?.[key]) ? columnFilters[key] : [])
    if (!facetas[key] && fetchFacetas) {
      try {
        const valores = await fetchFacetas(key)
        setFacetas(prev => ({ ...prev, [key]: valores }))
      } catch { setFacetas(prev => ({ ...prev, [key]: [] })) }
    }
  }

  const filtrosActivos = Object.entries(columnFilters || {}).filter(([, v]) => Array.isArray(v) ? v.length : v).length
  const limpiarFiltros = () => {
    setFiltrosLocal({})
    Object.keys(columnFilters || {}).forEach(k => onColumnFilter(k, ''))
    setFacetaAbierta(null)
  }

  // facetas DEPENDIENTES: si cambian los filtros, los valores posibles cambian → recargar
  useEffect(() => { setFacetas({}) }, [JSON.stringify(columnFilters)])

  // cerrar el desplegable con Escape
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') setFacetaAbierta(null) }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [])

  const handleDelete = async (row) => {
    if (confirmDelete?.id === row.id) {
      await onDelete(row)
      setConfirmDelete(null)
    } else {
      setConfirmDelete(row)
    }
  }

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>{title}</h2>
        <div style={styles.headerRight}>
          {showFilters && filtrosActivos > 0 && (
            <Boton variante="ghost" sm onClick={limpiarFiltros}>✕ Limpiar filtros ({filtrosActivos})</Boton>
          )}
          {typeof onExport === 'function' && (
            <Boton variante="secondary" sm onClick={onExport} title="Descarga en CSV todo lo que matchea los filtros actuales">
              ⬇ Exportar{pagination?.total ? ` (${pagination.total.toLocaleString('es-AR')})` : ''}
            </Boton>
          )}
          {showSearch && (
            <input
              style={styles.search}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder || 'Buscar...'}
            />
          )}
          <Boton variante="primary" onClick={onCreate}>+ Nuevo</Boton>
        </div>
      </div>
      {loading ? (
        <p style={{ color: '#64748b' }}>Cargando...</p>
      ) : (
        <div style={{ ...styles.tableWrapper, ...(scrollX ? styles.tableWrapperScroll : {}) }}>
          <table style={{ ...styles.table, ...(scrollX ? styles.tableScroll : {}) }}>
            <thead>
              <tr>
                {seleccionables && (
                  <th style={{ ...styles.th, width: 34 }}>
                    <input
                      type="checkbox"
                      title="Elegir todo lo que se ve"
                      checked={rows.length > 0 && rows.every(r => seleccion?.has(r.id))}
                      onChange={() => onToggleTodas?.(rows)}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                {columns.map((col, i) => (
                  <th key={col.key} style={{ ...styles.th, ...(col.w ? { minWidth: col.w } : {}), ...stickyCol(i, 5) }}>
                    <span style={styles.thInner}>
                      {col.label}
                      {scrollX && (
                        <button
                          style={{ ...styles.pinBtn, opacity: i <= pinIndex ? 1 : 0.25 }}
                          title={pinHasta === col.key ? 'Soltar columnas fijas' : `Fijar hasta "${col.label}"`}
                          onClick={() => togglePin(col.key)}
                        >📌</button>
                      )}
                    </span>
                  </th>
                ))}
                {!filaHref && <th style={styles.th}>Acciones</th>}
              </tr>
              {showFilters && (
                <tr>
                  {seleccionables && <td style={styles.thFiltro} />}
                  {columns.map((col, i) => {
                    const seleccion = Array.isArray(columnFilters?.[col.key]) ? columnFilters[col.key] : []
                    const sticky = stickyCol(i, 4)
                    return (
                      <td key={col.key} style={{ ...styles.thFiltro, ...sticky, position: sticky.position || 'relative' }}>
                        {col.filtroMultiple ? (
                          <>
                            <button
                              style={{ ...styles.facetaBtn, ...(seleccion.length ? styles.facetaBtnActivo : {}) }}
                              onClick={() => abrirFaceta(col.key)}
                            >
                              {seleccion.length ? `${seleccion.length} elegido${seleccion.length > 1 ? 's' : ''}` : 'Todos'} ▾
                            </button>
                            {facetaAbierta === col.key && (
                              <>
                                <div style={styles.facetaBackdrop} onClick={() => setFacetaAbierta(null)} />
                                <div style={styles.facetaPanel}>
                                  <input
                                    style={styles.facetaSearch}
                                    placeholder="Buscar valor…"
                                    value={facetaBusqueda}
                                    onChange={e => setFacetaBusqueda(e.target.value)}
                                    autoFocus
                                  />
                                  <div style={styles.facetaLista}>
                                    {(facetas[col.key] || []).filter(v => !facetaBusqueda || String(v).toLowerCase().includes(facetaBusqueda.toLowerCase())).map(v => (
                                      <label key={v} style={styles.facetaItem}>
                                        <input type="checkbox" checked={seleccionLocal.includes(v)} onChange={() => toggleFacetaValor(v)} />
                                        <span style={styles.facetaItemTexto}>{String(v)}</span>
                                      </label>
                                    ))}
                                    {!facetas[col.key] && <p style={styles.facetaCargando}>Cargando…</p>}
                                    {facetas[col.key]?.length === 0 && <p style={styles.facetaCargando}>Sin valores</p>}
                                  </div>
                                  <div style={styles.facetaAcciones}>
                                    {seleccionLocal.length > 0 && facetas[col.key]?.length > 0 && (
                                      <button
                                        style={styles.facetaInvertir}
                                        title="Tilda todos los valores MENOS los elegidos (sin ejecutar todavía)"
                                        onClick={() => setSeleccionLocal((facetas[col.key] || []).filter(v => !seleccionLocal.includes(v)))}
                                      >⇄ Invertir</button>
                                    )}
                                    {(seleccionLocal.length > 0 || seleccion.length > 0) && (
                                      <button style={styles.facetaLimpiar} onClick={() => { setSeleccionLocal([]); onColumnFilter(col.key, ''); setFacetaAbierta(null) }}>✕ Limpiar</button>
                                    )}
                                  </div>
                                  <button
                                    style={styles.facetaAplicar}
                                    onClick={() => { onColumnFilter(col.key, seleccionLocal.length ? seleccionLocal : ''); setFacetaAbierta(null) }}
                                  >
                                    ✓ Aplicar{seleccionLocal.length ? ` (${seleccionLocal.length})` : ' (todos)'}
                                  </button>
                                </div>
                              </>
                            )}
                          </>
                        ) : col.filtrable ? (
                          <input
                            style={styles.filtroInput}
                            value={filtrosLocal[col.key] ?? (typeof columnFilters?.[col.key] === 'string' ? columnFilters[col.key] : '')}
                            onChange={(e) => handleFiltroTexto(col.key, e.target.value)}
                            placeholder="Filtrar…"
                          />
                        ) : null}
                      </td>
                    )
                  })}
                  {!filaHref && <td style={styles.thFiltro} />}
                </tr>
              )}
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (seleccionables ? 1 : 0) + (filaHref ? 0 : 1)} style={styles.empty}>Sin registros</td>
                </tr>
              ) : rows.map(row => {
                const celdas = (
                  <>
                    {seleccionables && (
                      <td style={{ ...styles.td, width: 34 }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!seleccion?.has(row.id)}
                          onChange={() => onToggleFila?.(row.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    )}
                    {columns.map((col, i) => (
                      <td key={col.key} style={{ ...styles.td, ...(col.w ? { minWidth: col.w } : {}), ...stickyCol(i, 1), ...(scrollX && i <= pinIndex ? { background: colors.bg } : {}) }}>
                        {col.render ? col.render(row[col.key], row) : String(row[col.key] ?? '')}
                      </td>
                    ))}
                    {!filaHref && (
                      <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                        <Boton variante="secondary" sm style={{ marginRight: 6 }} onClick={() => onEdit(row)}>Editar</Boton>
                        <Boton
                          variante={confirmDelete?.id === row.id ? 'success' : 'destructive'}
                          sm
                          onClick={() => handleDelete(row)}
                        >
                          {confirmDelete?.id === row.id ? '✓ Confirmar' : 'Eliminar'}
                        </Boton>
                        {confirmDelete?.id === row.id && (
                          <Boton variante="ghost" sm style={{ marginLeft: 6 }} onClick={() => setConfirmDelete(null)}>Cancelar</Boton>
                        )}
                      </td>
                    )}
                  </>
                )

                return filaHref
                  ? <FilaLinkAbm key={row.id} href={filaHref(row)}>{celdas}</FilaLinkAbm>
                  : <tr key={row.id} style={styles.tr}>{celdas}</tr>
              })}
            </tbody>
          </table>
        </div>
      )}
      {pagination && pagination.last_page > 0 && (
        <div style={styles.pager}>
          <span style={styles.pagerInfo}>
            {pagination.total > 0
              ? `${pagination.from}–${pagination.to} de ${pagination.total}`
              : 'Sin resultados'}
          </span>
          <Paginador
            pagina={pagination.current_page}
            totalPaginas={pagination.last_page}
            onCambiar={onPageChange}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Fila que navega al detalle (patrón de /notas-venta): click normal navega,
 * Ctrl/⌘+click y click del medio abren pestaña nueva, sombra al pasar.
 */
function FilaLinkAbm({ href, children }) {
  const fila = useFilaLink(href)

  return (
    <tr
      {...fila}
      onMouseEnter={(e) => { e.currentTarget.style.background = colors.bgSoft }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '' }}
      style={{ ...styles.tr, ...fila.style }}
    >
      {children}
    </tr>
  )
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.xl, gap: space.xl },
  headerRight: { display: 'flex', alignItems: 'center', gap: space.md },
  search: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, width: 260, fontFamily: 'inherit', background: colors.bg },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },

  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: space.lg, flexWrap: 'wrap', gap: space.md },
  pagerInfo: { fontSize: text.sec, color: colors.textSec },
  pagerBtns: { display: 'flex', alignItems: 'center', gap: space.md },
  pagerPage: { fontSize: text.sec, color: '#475569', fontWeight: 600 },
  pagerBtn: { padding: '6px 14px', background: colors.bg, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: text.body, color: colors.text, fontWeight: 500 },
  pagerBtnDisabled: { color: colors.textPlh, cursor: 'not-allowed', background: colors.bgSoft },

  tableWrapper: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'hidden', fontVariantNumeric: 'tabular-nums' },
  tableWrapperScroll: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: text.body },
  tableScroll: { width: 'max-content', minWidth: '100%' },
  th: { padding: '8px 12px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  thInner: { display: 'inline-flex', alignItems: 'center', gap: 4 },
  pinBtn: { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, padding: 0, lineHeight: 1 },
  thFiltro: { padding: '4px 8px', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}` },
  filtroInput: { width: '100%', minWidth: 55, padding: '3px 8px', border: `1px solid ${colors.borderSoft}`, borderRadius: radius.sm, fontSize: text.sec, fontFamily: 'inherit', background: colors.bg, boxSizing: 'border-box' },

  facetaBtn: { width: '100%', padding: '3px 8px', border: `1px solid ${colors.borderSoft}`, borderRadius: radius.sm, fontSize: text.sec, background: colors.bg, cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: colors.textSec },
  facetaBtnActivo: { borderColor: '#93c5fd', background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 },
  facetaBackdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  facetaPanel: { position: 'absolute', top: '100%', left: 0, zIndex: 50, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: '0 8px 24px rgba(15,23,42,0.15)', width: 230, padding: 8, textTransform: 'none' },
  facetaSearch: { width: '100%', padding: '4px 8px', border: `1px solid ${colors.borderSoft}`, borderRadius: radius.sm, fontSize: text.sec, boxSizing: 'border-box', marginBottom: 6, fontFamily: 'inherit' },
  facetaLista: { height: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 },
  facetaItem: { display: 'flex', alignItems: 'center', gap: 6, padding: '3px 4px', fontSize: text.sec, cursor: 'pointer', borderRadius: 4 },
  facetaItemTexto: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.text },
  facetaCargando: { margin: 4, fontSize: text.sec, color: colors.textMeta },
  facetaAcciones: { display: 'flex', gap: 4, marginTop: 6 },
  facetaInvertir: { flex: 1, padding: '4px 8px', border: `1px solid ${colors.borderSoft}`, borderRadius: radius.sm, background: colors.bg, color: '#1d4ed8', fontSize: text.sec, cursor: 'pointer', fontWeight: 600 },
  facetaAplicar: { marginTop: 6, width: '100%', padding: '6px 8px', border: 'none', borderRadius: radius.sm, background: '#1e40af', color: '#fff', fontSize: text.sec, cursor: 'pointer', fontWeight: 600 },
  facetaLimpiar: { flex: 1, padding: '4px 8px', border: 'none', background: 'transparent', color: '#b91c1c', fontSize: text.sec, cursor: 'pointer', fontWeight: 600, textAlign: 'center' },

  tr: { height: 44 },
  td: { padding: '8px 12px', borderBottom: `1px solid ${colors.borderSoft}`, color: colors.text, whiteSpace: 'nowrap' },
  empty: { padding: 24, textAlign: 'center', color: colors.textMeta },
}
